import React, { useCallback, useMemo, useState } from "react";
import {
  Alert,
  Button,
  Flex,
  Input,
  Popconfirm,
  Select,
  Space,
  Typography,
} from "antd";
import { CloseOutlined } from "@ant-design/icons";
import type { MessageInstance } from "antd/es/message/interface";
import { GPS, TrapSite } from "../../../types";
import {
  useCreateTrapSiteMutation,
  useDeleteTrapSiteMutation,
  useUpdateTrapSiteMutation,
} from "../../../actions/trapsiteInQuery";
import { useUpdateDeviceMutation } from "../../../actions/devicesInQuery";
import { formatDistance } from "../../../utils/geo";
import { useTrapsiteEditorContext } from "./TrapsiteEditorContext";
import { EditorDevice } from "./types";
import { assessTrapProximity, nearbyPlacement } from "./trapProximity";
import { errorMessage } from "./errorMessage";
import {
  applyStagedSites,
  hasStagedMoves,
  stagedSiteEntries,
} from "./stagedMoves";
import { PANEL_Z_INDEX, POPUP_Z_INDEX } from "./constants";

const { Title, Text } = Typography;

interface Props {
  devices: EditorDevice[];
  trapSites: Partial<TrapSite>[];
  container: HTMLElement | null;
  messageApi: MessageInstance;
  onFramePoint: (gps: GPS) => void;
}

/**
 * Place or reposition a trap.
 *
 * Reached two ways. "Edit Trap" starts with a trap chosen; "Move Trap" starts
 * from a point on the map with no trap chosen yet, so the point is the thing
 * being asserted and picking a trap must not overwrite it.
 */
const TrapEditorPanel: React.FC<Props> = ({
  devices,
  trapSites,
  container,
  messageApi,
  onFramePoint,
}) => {
  const editor = useTrapsiteEditorContext();
  const draft = editor.deviceDraft!;

  const [saveError, setSaveError] = useState<string | null>(null);
  const [newSiteName, setNewSiteName] = useState("");
  /** A location created from here, so cancelling can take it back out. */
  const [createdSiteId, setCreatedSiteId] = useState<string | null>(null);

  const [updateDevice, { isLoading: saving }] = useUpdateDeviceMutation();
  const [createTrapSite, { isLoading: creatingSite }] =
    useCreateTrapSiteMutation();
  const [deleteTrapSite] = useDeleteTrapSiteMutation();
  const [updateTrapSite] = useUpdateTrapSiteMutation();

  const selected = useMemo(
    () => devices.find((d) => d.uuid === draft.deviceUuid) ?? null,
    [devices, draft.deviceUuid],
  );

  // Positions as they appear on the map, so dragging a location onto its trap
  // clears the warning instead of leaving a stale one standing.
  const visibleSites = useMemo(
    () => applyStagedSites(trapSites, editor.staged),
    [trapSites, editor.staged],
  );

  const proximity = useMemo(
    () =>
      // Nothing is being placed until a trap is chosen, so there is nothing to
      // be too far from - the pin is just a point on the map.
      draft.deviceUuid
        ? assessTrapProximity(draft.gps, draft.trapSiteId, visibleSites)
        : ({ kind: "unknown" } as const),
    [draft.deviceUuid, draft.gps, draft.trapSiteId, visibleSites],
  );

  /**
   * The suggested location is the one the trap is already recorded at, so
   * "moving" it there would change nothing.
   */
  const alreadyThere =
    proximity.kind === "far-but-covered" &&
    (proximity.covering.id === selected?.trapSiteId ||
      proximity.covering.id === draft.trapSiteId);

  // Dragging the location's pin is an edit to this form too.
  const dirty =
    hasStagedMoves(editor.staged) ||
    (!!draft.deviceUuid &&
      (selected === null ||
        draft.displayName.trim() !== selected.displayName ||
        draft.trapSiteId !== selected.trapSiteId ||
        draft.gps.lat !== selected.gps?.lat ||
        draft.gps.lon !== selected.gps?.lon));

  // A trap needs a name before it can be saved; a staged drag of some other
  // pin does not, and must not be lost just because no trap is chosen yet.
  const canSave =
    dirty && (!draft.deviceUuid || draft.displayName.trim() !== "");

  const handleAdoptNearest = useCallback(
    (siteId: string) => editor.patchDeviceDraft({ trapSiteId: siteId }),
    [editor],
  );

  const handleCreateSite = useCallback(async () => {
    const name = newSiteName.trim();
    if (!name) return;
    setSaveError(null);
    try {
      const result = await createTrapSite({
        display_name: name,
        // Beside the trap rather than underneath it, so both pins stay
        // visible and separately draggable. The backend reverse-geocodes this
        // to fill in the address and its structured components.
        gps: nearbyPlacement(draft.gps),
      }).unwrap();
      setCreatedSiteId(result._id);
      setNewSiteName("");
      editor.patchDeviceDraft({ trapSiteId: result._id });
      messageApi.success(`Created ${name}`);
    } catch (err) {
      setSaveError(errorMessage(err, "Could not create that location."));
    }
  }, [newSiteName, createTrapSite, draft.gps, editor, messageApi]);

  /**
   * Take back a location created from this panel.
   *
   * Cancelling must not leave a location nobody asked for. Deletion is soft,
   * so this is the same reversible operation the trap site editor performs.
   */
  const discardCreatedSite = useCallback(async () => {
    if (!createdSiteId) return;
    try {
      await deleteTrapSite(createdSiteId).unwrap();
    } catch {
      // Best effort: failing to tidy up must not trap the user in the panel.
    }
  }, [createdSiteId, deleteTrapSite]);

  const handleCancel = useCallback(async () => {
    await discardCreatedSite();
    editor.close();
  }, [discardCreatedSite, editor]);

  const submit = useCallback(async () => {
    setSaveError(null);
    try {
      if (draft.deviceUuid) {
        await updateDevice({
          deviceUuid: draft.deviceUuid,
          display_name: draft.displayName.trim(),
          gps: draft.gps,
          trap_site_id: draft.trapSiteId,
        }).unwrap();
      }

      // Positions of the other pins the user dragged while they were here.
      // Staged rather than written on drop, so nothing changes until Save.
      for (const [uuid, gps] of Object.entries(editor.staged.devices)) {
        if (uuid === draft.deviceUuid) continue;
        await updateDevice({ deviceUuid: uuid, gps }).unwrap();
      }
      for (const [id, gps] of stagedSiteEntries(editor.staged)) {
        await updateTrapSite({ id, gps }).unwrap();
      }

      messageApi.success(draft.deviceUuid ? "Trap saved" : "Positions saved");
      // Keep the created location: saving is what it was created for.
      setCreatedSiteId(null);
      editor.close();
    } catch (err) {
      setSaveError(errorMessage(err, "Could not save this trap."));
    }
  }, [updateDevice, updateTrapSite, draft, editor, messageApi]);

  const siteOptions = useMemo(
    () =>
      trapSites
        .filter((s) => s._id)
        .map((s) => ({
          value: s._id as string,
          label: s.display_name || "Unnamed location",
        })),
    [trapSites],
  );

  return (
    <div className="trapsite-editor-panel" style={{ zIndex: PANEL_Z_INDEX }}>
      <Flex
        justify="space-between"
        align="center"
        gap="small"
        className="trapsite-editor-header"
      >
        <Title level={5} className="m-0 truncate">
          {editor.mode === "edit" ? "Edit Trap" : "Move Trap"}
        </Title>
        <Button
          type="text"
          aria-label="Close"
          icon={<CloseOutlined />}
          onClick={handleCancel}
        />
      </Flex>

      <div className="trapsite-editor-scroll">
        <Flex vertical gap="middle" className="trapsite-editor-body">
          <div className="flex flex-col gap-2 min-w-0">
            <Text type="secondary">Trap</Text>
            <Select
              className="w-full"
              placeholder="Choose a trap"
              value={draft.deviceUuid}
              options={devices.map((d) => ({
                value: d.uuid,
                label: d.trapSiteName
                  ? `${d.displayName} - ${d.trapSiteName}`
                  : d.displayName,
              }))}
              onChange={(uuid: string) =>
                editor.selectDevice(
                  devices.find((d) => d.uuid === uuid) ?? null,
                )
              }
              showSearch
              optionFilterProp="label"
              getPopupContainer={() => container || document.body}
              styles={{ popup: { root: { zIndex: POPUP_Z_INDEX } } }}
            />
          </div>

          <div className="flex flex-col gap-2 min-w-0">
            <Text type="secondary">Name</Text>
            <Input
              value={draft.displayName}
              placeholder="Trap name"
              disabled={!draft.deviceUuid}
              onChange={(e) =>
                editor.patchDeviceDraft({ displayName: e.target.value })
              }
            />
          </div>

          <div className="flex flex-col gap-2 min-w-0">
            <Text type="secondary">Collection location</Text>
            <Select
              className="w-full"
              placeholder="Unassigned"
              value={draft.trapSiteId}
              options={siteOptions}
              allowClear
              onChange={(id: string | undefined) =>
                editor.patchDeviceDraft({ trapSiteId: id ?? null })
              }
              showSearch
              optionFilterProp="label"
              getPopupContainer={() => container || document.body}
              styles={{ popup: { root: { zIndex: POPUP_Z_INDEX } } }}
            />
          </div>

          <Text type="secondary" className="text-xs">
            Drag any pin on the map to move it.
          </Text>

          {proximity.kind === "far-but-covered" && (
            <Alert
              type="warning"
              showIcon
              message={`This trap is ${formatDistance(proximity.distanceMeters)} from ${proximity.current.displayName}, outside its ${formatDistance(proximity.current.radiusMeters)} range.`}
              description={
                <Space direction="vertical" size="small">
                  {/* "Covers", not "is closer": a wider location further away
                      can contain a trap that a nearer one does not, and
                      calling it closer would simply be untrue. */}
                  <Text>
                    {proximity.covering.displayName} covers this position (
                    {formatDistance(proximity.covering.distanceMeters)} away).
                  </Text>
                  <Button
                    size="small"
                    type="primary"
                    disabled={alreadyThere}
                    onClick={() => handleAdoptNearest(proximity.covering.id)}
                  >
                    {alreadyThere
                      ? `Already in ${proximity.covering.displayName}`
                      : `Move it to ${proximity.covering.displayName}`}
                  </Button>
                </Space>
              }
            />
          )}

          {proximity.kind === "stranded" && (
            <Alert
              type="warning"
              showIcon
              message={`This trap is ${formatDistance(proximity.distanceMeters)} from ${proximity.current.displayName}, outside its ${formatDistance(proximity.current.radiusMeters)} range.`}
              description={
                <Space direction="vertical" size="small" className="w-full">
                  <Text>
                    No collection location covers this position. Create one
                    here?
                  </Text>
                  <Space.Compact className="w-full">
                    <Input
                      value={newSiteName}
                      placeholder="New location name"
                      onChange={(e) => setNewSiteName(e.target.value)}
                      onPressEnter={handleCreateSite}
                    />
                    <Button
                      type="primary"
                      loading={creatingSite}
                      disabled={!newSiteName.trim()}
                      onClick={handleCreateSite}
                    >
                      Create
                    </Button>
                  </Space.Compact>
                </Space>
              }
            />
          )}

          {saveError && (
            <Alert
              type="error"
              showIcon
              message={saveError}
              closable
              onClose={() => setSaveError(null)}
            />
          )}

          <Flex justify="end" align="center" gap="small" className="mt-2">
            <Button onClick={handleCancel}>Cancel</Button>
            <Popconfirm
              // Reassigning a trap moves it off whatever location it was on,
              // which is the same consequence the trap site editor confirms.
              disabled={!selected || draft.trapSiteId === selected.trapSiteId}
              title="Change collection location?"
              description={
                <div className="max-w-64">
                  {selected?.trapSiteName
                    ? `${selected.displayName} moves off ${selected.trapSiteName}.`
                    : `${selected?.displayName ?? "This trap"} will be assigned to a location.`}
                </div>
              }
              okText="Confirm"
              cancelText="Cancel"
              getPopupContainer={() => container || document.body}
              zIndex={POPUP_Z_INDEX}
              onConfirm={submit}
            >
              <Button
                type="primary"
                loading={saving}
                disabled={!canSave}
                onClick={() => {
                  // Popconfirm does not swallow its child's onClick, so
                  // without this guard an unchanged location would both open
                  // the confirm and save at once.
                  if (!selected || draft.trapSiteId === selected.trapSiteId) {
                    submit();
                  }
                }}
              >
                Save
              </Button>
            </Popconfirm>
          </Flex>
        </Flex>
      </div>
    </div>
  );
};

export default TrapEditorPanel;
